import apiClient from './apiClient';
import type { ApiResponse } from '../types/common';
import type { ReportType } from '../types/documentTypes.generated';
import type { NivelDeAplicacao } from '../types/nivelDeAplicacao';

/**
 * O que vale para um destino — o que foi aplicado nele e o que ele herda dos níveis de cima —,
 * lido do endpoint de efetivos (#606). A tela não recalcula a herança: quem decide a
 * precedência é o servidor, o mesmo motor que monta os documentos.
 */
export type FamiliaDeAplicacao = 'risk' | 'source' | 'exam';

/** Ids chegam como string do banco (`bigNumberStrings`): compare sempre convertido. */
export type IdDoBanco = number | string;

export interface ReferenciaDeNivel {
  nivel: NivelDeAplicacao;
  id: IdDoBanco;
  nome: string;
}

export interface DestinoEfetivo extends ReferenciaDeNivel {
  empresaId: IdDoBanco;
  empresaNome: string;
}

export interface Heranca extends ComCompletude {
  origem: ReferenciaDeNivel;
  /** Existe aplicação própria do destino com a mesma chave: a de cima não vale aqui. */
  substituidaNoDestino: boolean;
  /** Só exames, quando a convocação segue outro nível que não o dos documentos. */
  convocacaoSegue?: ReferenciaDeNivel;
}

export type AplicacaoHerdada<A> = A & Heranca;

/**
 * Quanto do que se aplica à aplicação já está preenchido, contado pelo servidor. `total` zero
 * quer dizer que nada se aplica; `faltam` traz o nome de cada informação que falta.
 */
export interface Completude {
  preenchidas: number;
  total: number;
  faltam: string[];
}

/** Cada aplicação dos efetivos traz a completude; o servidor antigo não manda, e aí não há barra. */
export interface ComCompletude {
  completude?: Completude;
}

export interface Efetivos<A> {
  destino: DestinoEfetivo;
  alcance: { funcionarios: number };
  proprias: (A & ComCompletude)[];
  herdadas: AplicacaoHerdada<A>[];
  /** Só riscos; ausente quando não há maioria entre os riscos marcados do alcance. */
  marcacaoSugerida?: ReportType[];
}

function desembrulhar<T>(resposta: ApiResponse<T>): T {
  if (resposta.data !== undefined) return resposta.data;
  return resposta as unknown as T;
}

export async function buscarEfetivos<A>(
  familia: FamiliaDeAplicacao,
  nivel: NivelDeAplicacao,
  destinoId: IdDoBanco
): Promise<Efetivos<A>> {
  const parametros = new URLSearchParams({ nivel, destinoId: String(destinoId) });
  const resposta = await apiClient.get<Efetivos<A>>(
    `/${familia}-applications/efetivas?${parametros.toString()}`
  );
  return desembrulhar(resposta);
}

export type SituacaoAposentadoriaEspecial =
  | 'calculado'
  | 'sem-riscos'
  | 'tempos-diferentes'
  | 'risco-sem-tempo';

export interface RiscoDoCargo {
  /** Já numérico: o servidor converte antes de enviar, ao contrário dos ids das aplicações. */
  riskId: number;
  nome: string;
  anos: number | null;
}

/** O que os riscos vigentes do cargo dizem sobre o Código GFIP e o Enquadramento (#302). */
export interface AposentadoriaEspecialDoCargo {
  situacao: SituacaoAposentadoriaEspecial;
  codigo: 1 | 2 | 3 | 4 | null;
  anos: number | null;
  riscos: RiscoDoCargo[];
}

export async function buscarAposentadoriaEspecial(
  cargoId: IdDoBanco
): Promise<AposentadoriaEspecialDoCargo> {
  const parametros = new URLSearchParams({ nivel: 'occupation', destinoId: String(cargoId) });
  const resposta = await apiClient.get<AposentadoriaEspecialDoCargo>(
    `/risk-applications/efetivas/aposentadoria-especial?${parametros.toString()}`
  );
  return desembrulhar(resposta);
}
